import { Html } from '@react-three/drei';
import type { LogEntry } from '../types';
import { severityHex } from '../utils/colors';

interface HolographicLogCardProps {
  log: LogEntry | null;
  orgSlug: string | null;
  telemetryOrigin: string;
  onClose: () => void;
}

export default function HolographicLogCard({
  log,
  orgSlug,
  telemetryOrigin,
  onClose
}: HolographicLogCardProps) {
  if (!log) {
    return null;
  }

  const timestamp = new Date(log.timestamp).toISOString();
  const traceHref =
    orgSlug && log.traceId
      ? `${telemetryOrigin}/${encodeURIComponent(orgSlug)}/query/v1/trace/${encodeURIComponent(log.traceId)}`
      : null;

  return (
    <group position={[0, 1.55, -1.35]}>
      <Html transform occlude center distanceFactor={1.15}>
        <div className="w-[22rem] rounded-2xl border border-cyan-400/30 bg-slate-950/88 p-4 text-slate-100 shadow-holo backdrop-blur-sm">
          <div className="mb-3 flex items-start justify-between gap-2">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-cyan-200/80">Selected Log</p>
              <p className="mt-1 text-sm font-bold text-cyan-50">{log.serviceName}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-white/15 px-2 py-1 text-xs text-slate-200 transition hover:border-cyan-200/60 hover:text-cyan-100"
            >
              Close
            </button>
          </div>

          <div className="space-y-2 text-xs">
            <p className="font-mono text-slate-300">
              Severity:{' '}
              <span className="font-bold" style={{ color: severityHex(log.severity) }}>
                {log.severity.toUpperCase()} ({log.severityNumber})
              </span>
            </p>
            <p className="font-mono text-slate-400">{timestamp}</p>
            <p className="rounded-xl border border-white/10 bg-slate-900/80 p-3 text-sm text-slate-100">{log.body}</p>
            <p className="font-mono text-slate-300">traceId: {log.traceId ?? 'n/a'}</p>
            <p className="font-mono text-slate-300">spanId: {log.spanId ?? 'n/a'}</p>
            <div className="flex flex-wrap gap-2 pt-1">
              {traceHref ? (
                <a
                  className="rounded-md border border-cyan-300/35 px-2 py-1 font-mono text-[11px] text-cyan-100 transition hover:bg-cyan-300/15"
                  href={traceHref}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open Trace
                </a>
              ) : null}
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(JSON.stringify(log.raw, null, 2));
                }}
                className="rounded-md border border-white/20 px-2 py-1 font-mono text-[11px] text-slate-100 transition hover:border-white/35 hover:bg-white/8"
              >
                Copy JSON
              </button>
            </div>
          </div>
        </div>
      </Html>
    </group>
  );
}
